import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, ApiError } from '../../api';
import { router } from '../../router';
import { showToast } from '../../components/ui/toast';
import { t } from '../../i18n';
import type { Column } from '../../components/ui/table';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const PALETTE = ['#2563eb', '#16a34a', '#d97706', '#9333ea', '#dc2626', '#0891b2', '#db2777'];

@customElement('cadence-classes-page')
export class CadenceClassesPage extends LitElement {
  @state() private classes: any[] = [];
  @state() private teachers: any[] = [];
  @state() private types: any[] = [];
  @state() private levels: any[] = [];
  @state() private view: 'grid' | 'list' = 'grid';
  @state() private loading = true;
  @state() private showCreate = false;
  @state() private form: any = { name: '', dance_type_id: '', level_id: '', teacher_id: '', day_of_week: 'monday', start_time: '18:00', end_time: '19:00', capacity: '' };
  @state() private formError = '';
  @state() private saving = false;

  static styles = css`
    :host { display: block; }
    .head { display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--space-4); gap: 12px; flex-wrap: wrap; }
    h1 { margin: 0; font-size: 1.6rem; }
    .actions { display: flex; gap: 10px; align-items: center; }
    .seg { display: inline-flex; border: 1px solid var(--color-border); border-radius: var(--radius-md); overflow: hidden; }
    .seg button { font: inherit; border: none; background: var(--color-bg-primary); color: var(--color-text-secondary); padding: 8px 14px; cursor: pointer; }
    .seg button.active { background: var(--color-primary); color: #fff; }
    .grid { display: grid; grid-template-columns: repeat(7, minmax(140px, 1fr)); gap: 8px; overflow-x: auto; }
    .day { background: var(--color-bg-secondary); border-radius: var(--radius-md); padding: 8px; min-height: 120px; }
    .day h4 { margin: 0 0 8px; font-size: 0.78rem; text-transform: uppercase; color: var(--color-text-secondary); text-align: center; }
    .cls { background: var(--color-bg-primary); border-left: 4px solid var(--c); border-radius: var(--radius-sm); padding: 8px; margin-bottom: 8px; cursor: pointer; box-shadow: var(--shadow-md); }
    .cls:hover { transform: translateY(-1px); }
    .cls .n { font-weight: 600; font-size: 0.85rem; }
    .cls .d { color: var(--color-text-secondary); font-size: 0.75rem; }
    .form { display: flex; flex-direction: column; gap: var(--space-3); }
    .form-error { color: var(--color-danger); font-size: 0.85rem; margin: 0; }
    .two { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }

  private async load() {
    this.loading = true;
    try {
      const [classes, teachers, types, levels] = await Promise.all([
        api.get<any[]>('/classes'), api.get<any[]>('/teachers'), api.get<any[]>('/dance/types'), api.get<any[]>('/dance/levels'),
      ]);
      this.classes = classes; this.teachers = teachers; this.types = types; this.levels = levels;
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private colorFor(typeId: string): string {
    const idx = Math.max(0, this.types.findIndex((t) => t.id === typeId));
    return PALETTE[idx % PALETTE.length];
  }

  private async create() {
    if (!this.form.name) { this.formError = t('classes.form.nameRequired'); return; }
    this.saving = true; this.formError = '';
    try {
      await api.post('/classes', { ...this.form, capacity: this.form.capacity === '' ? null : Number(this.form.capacity) });
      showToast(t('classes.toast.created'), 'success');
      this.showCreate = false;
      await this.load();
    } catch (e) { this.formError = e instanceof ApiError ? e.message : t('classes.form.saveFailed'); }
    finally { this.saving = false; }
  }

  private columns: Column[] = [
    { key: 'name', label: t('classes.list.col.name'), sortable: true },
    { key: 'teacher_name', label: t('classes.list.col.teacher') },
    { key: 'dance_type_name', label: t('classes.list.col.type') },
    { key: 'level_name', label: t('classes.list.col.level') },
    { key: 'day_of_week', label: t('classes.list.col.day'), render: (c) => c.day_of_week ? t(`classes.days.${c.day_of_week}`) : '' },
    { key: 'start_time', label: t('classes.list.col.time'), render: (c) => `${c.start_time}–${c.end_time}` },
    { key: 'enrolled_count', label: t('classes.list.col.enrolled'), align: 'right', render: (c) => `${c.enrolled_count}${c.capacity != null ? '/' + c.capacity : ''}` },
  ];

  private renderGrid() {
    return html`<div class="grid">
      ${DAYS.map((day) => html`
        <div class="day">
          <h4>${t(`classes.daysShort.${day}`)}</h4>
          ${this.classes.filter((c) => c.day_of_week === day).sort((a, b) => (a.start_time ?? '').localeCompare(b.start_time ?? '')).map((c) => html`
            <div class="cls" style="--c:${this.colorFor(c.dance_type_id)}" @click=${() => router.navigate(`/admin/classes/${c.id}`)}>
              <div class="n">${c.name}</div>
              <div class="d">${c.teacher_name ?? '—'}</div>
              <div class="d">${c.start_time}–${c.end_time} · ${c.enrolled_count}${c.capacity != null ? '/' + c.capacity : ''}</div>
            </div>`)}
        </div>`)}
    </div>`;
  }

  render() {
    return html`
      <div class="head">
        <h1>${t('classes.list.title')}</h1>
        <div class="actions">
          <div class="seg">
            <button class=${this.view === 'grid' ? 'active' : ''} @click=${() => (this.view = 'grid')}>${t('classes.list.timetable')}</button>
            <button class=${this.view === 'list' ? 'active' : ''} @click=${() => (this.view = 'list')}>${t('classes.list.list')}</button>
          </div>
          <cadence-button @click=${() => { this.showCreate = true; this.formError = ''; }}>${t('classes.list.addClass')}</cadence-button>
        </div>
      </div>

      ${this.view === 'grid'
        ? this.renderGrid()
        : html`<cadence-table clickable .columns=${this.columns} .rows=${this.classes}
            @row-click=${(e: CustomEvent) => router.navigate(`/admin/classes/${e.detail.id}`)}></cadence-table>`}

      <cadence-modal ?open=${this.showCreate} heading=${t('classes.form.heading')} @close=${() => (this.showCreate = false)}>
        <div class="form">
          ${this.formError ? html`<p class="form-error" role="alert">${this.formError}</p>` : ''}
          <cadence-input label=${t('classes.form.name')} .value=${this.form.name} @input=${(e: CustomEvent) => (this.form = { ...this.form, name: e.detail })}></cadence-input>
          <div class="two">
            <cadence-select label=${t('classes.form.danceType')} placeholder=${t('classes.form.danceTypePlaceholder')} .value=${this.form.dance_type_id}
              .options=${this.types.map((t) => ({ value: t.id, label: t.name }))}
              @change=${(e: CustomEvent) => (this.form = { ...this.form, dance_type_id: e.detail })}></cadence-select>
            <cadence-select label=${t('classes.form.level')} placeholder=${t('classes.form.levelPlaceholder')} .value=${this.form.level_id}
              .options=${this.levels.map((l) => ({ value: l.id, label: l.name }))}
              @change=${(e: CustomEvent) => (this.form = { ...this.form, level_id: e.detail })}></cadence-select>
          </div>
          <cadence-select label=${t('classes.form.teacher')} placeholder=${t('classes.form.teacherPlaceholder')} .value=${this.form.teacher_id}
            .options=${this.teachers.map((tc) => ({ value: tc.id, label: `${tc.first_name} ${tc.last_name}` }))}
            @change=${(e: CustomEvent) => (this.form = { ...this.form, teacher_id: e.detail })}></cadence-select>
          <div class="two">
            <cadence-select label=${t('classes.form.day')} .value=${this.form.day_of_week}
              .options=${DAYS.map((d) => ({ value: d, label: t(`classes.days.${d}`) }))}
              @change=${(e: CustomEvent) => (this.form = { ...this.form, day_of_week: e.detail })}></cadence-select>
            <cadence-input label=${t('classes.form.capacity')} type="number" .value=${this.form.capacity} @input=${(e: CustomEvent) => (this.form = { ...this.form, capacity: e.detail })}></cadence-input>
          </div>
          <div class="two">
            <cadence-input label=${t('classes.form.startTime')} .value=${this.form.start_time} @input=${(e: CustomEvent) => (this.form = { ...this.form, start_time: e.detail })}></cadence-input>
            <cadence-input label=${t('classes.form.endTime')} .value=${this.form.end_time} @input=${(e: CustomEvent) => (this.form = { ...this.form, end_time: e.detail })}></cadence-input>
          </div>
        </div>
        <div slot="footer">
          <cadence-button variant="ghost" @click=${() => (this.showCreate = false)}>${t('common.actions.cancel')}</cadence-button>
          <cadence-button ?loading=${this.saving} @click=${() => this.create()}>${t('classes.form.create')}</cadence-button>
        </div>
      </cadence-modal>
    `;
  }
}
